/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute } from '@angular/router';
import { AdcModel, BackupModel } from '../../../../shared/models';
import { AdcsApiService, BackupApiService } from '../../../../shared/http';
import { BackupUtilsService } from '../../../../shared/services/component';
import { BaseComponent } from '../../../../../../core/components';
import { ChangeDetectorRef, Component, OnDestroy, ViewChild, ViewContainerRef } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
    KeyStoreService,
    LoadingService,
    LogService,
    ModalService,
    NotifyService,
    RbacService,
    UserService,
    UtilsService
} from '../../../../../../core/services';
import { NamespaceService } from '../../../../shared/services/utils';
import { VendorEnum } from '../../../../shared/enums';
import { forkJoin, Observable, Subscriber } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-backup-details',
    templateUrl: './backup-details.component.html',
    styleUrls: ['./backup-details.component.scss']
})

export class BackupDetailsComponent extends BaseComponent implements OnDestroy {
    /**
     * Reference to new service container.
     */
    @ViewChild('modalContainer', { read: ViewContainerRef, static: true })
    public modalContainer: ViewContainerRef;

    /**
     * Store the backupId from the query params.
     */
    public adcId: string;

    /**
     * Store the ADC modal.
     */
    public adc: AdcModel;

    /**
     * New organisation form group.
     */
    public passwordForm: FormGroup;

    /**
     * Table row data
     */
    public tableRows: any[] = [];

    /**
     * Temporary list of table objects used for searching (Table ONLY)
     */
    public tempRows: any[] = [];

    /**
     * Search term for searching the services table, also used with the search query parameter in the url
     */
    public searchTerm: string = '';

    /**
     * Store the user's ability to edit
     */
    public userCanEdit: boolean = false;

    /**
     * Store the ID of the appliance to be copied.
     */
    public copyApplianceId: string = '';

    /**
     * State of the modal ok button.
     */
    public modalButtonDisabled: boolean = true;

    /**
     * Namespace selected key.
     * @private
     */
    private selectedNamespaceKey: number;

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    constructor(
        public modalService: ModalService,
        private activatedRoute: ActivatedRoute,
        private adcsApiService: AdcsApiService,
        private backupApiService: BackupApiService,
        private backupUtilsService: BackupUtilsService,
        private cdr: ChangeDetectorRef,
        private formBuilder: FormBuilder,
        private keyStoreService: KeyStoreService,
        private loadingService: LoadingService,
        private logService: LogService,
        private namespaceService: NamespaceService,
        private notifyService: NotifyService,
        private rbacService: RbacService,
        private userService: UserService,
        private utilsService: UtilsService
    ) {
        super();

        this.selectedNamespaceKey = this.namespaceService.selectedNamespace.key;
        this.namespaceService.disableEmitter.next(true);

        const id: string = this.activatedRoute.snapshot.paramMap.get('id');
        if (id) {
            this.adcId = id;
        }

        this.setupUserValues();
        // Set up the form first.
        this.setupForms();

        this.setUpPageData();
        this.setupSubscribers();
        this.registerButtons();
    }

    /**
     * Set the name space dropdown enabled again.
     */
    public ngOnDestroy(): void {
        this.namespaceService.disableEmitter.next(false);
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    /**
     * Confirm copy button clicked
     */
    public confirmModalCopy(): void {
        this.modalButtonDisabled = false;
    }

    /**
     * Start download .
     * @param row
     */
    public startDownload(row: any): void {
        this.backupUtilsService.startDownload(row, this.notifyService, this.modalContainer)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.copyApplianceId = row.applianceId;
                    this.modalService.close('confirm-password');
                    if (row.adcVendorId === VendorEnum.LBENTERPRISE || row.adcVendorId === VendorEnum.KEMP) {
                        this.modalService.openModalInParent('copy-appliance-id');
                    }
                    this.logService.info('BackupDetailsComponent: startDownload', 'Download complete.');
                    this.loadingService.loadingActionDotsEmitter.next(false);
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('BackupDetailsComponent: startDownload', error.message);
                }
            });
    }

    /**
     * Confirm delete backup.
     * @param row
     */
    public confirmDeleteBackup(row: BackupModel): void {
        const confirmationObject = {
            title: 'delete_backup',
            message: 'delete_backup_message',
            callBack: this.deleteBackup.bind(this, row),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Delete backup.
     * @param row
     */
    private deleteBackup(row: BackupModel): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.backupApiService.deleteBackup(this.selectedNamespaceKey, row.id.toString())
                .subscribe({
                    next: (): void => {
                        this.setUpPageData();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error(
                            'StorageBackupsComponent: deleteBackup',
                            'Error deleting backup', error
                        );
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Set up user values.
     * @private
     */
    private setupUserValues(): void {
        const user = this.userService.getUser();
        this.userCanEdit = user.permissions && (user.permissions < 4 && user.permissions !== 0);
    }

    /**
     * Set up forms.
     * @private
     */
    private setupForms(): void {
        this.passwordForm = this.formBuilder.group({
            password: [null, [Validators.required]]
        });
    }

    /**
     * Get all backups for a name space
     * @private
     */
    private setUpPageData(): void {
        const apiCalls: any = {
            backups: this.backupApiService.getBackupsByNamespaceId(this.selectedNamespaceKey),
            adcs: this.adcsApiService.getAll(this.selectedNamespaceKey)
        };

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroup: object): any => {
                    // TODO update ADC api call to get only the ADC we are looking at.
                    this.adc = dataGroup['adcs'].find((item: AdcModel): boolean => item.id.toString() === this.adcId);
                    this.cdr.detectChanges();
                    this.populateTableRows(dataGroup['backups'], this.adc);
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'getAllBackups',
                        'Error get backups for namespace' + this.selectedNamespaceKey,
                        error
                    );
                }
            });
    }

    /**
     * Populate table rows.
     * @param backups
     * @param adc
     * @private
     */
    private populateTableRows(backups: BackupModel[], adc: AdcModel): void {
        this.tableRows = [];
        const filterBackups: BackupModel[] = backups.filter(item => item.adcID === adc.id);

        for (const backup of filterBackups) {
            this.tableRows.push({
                id: backup.id,
                productType: 'ADC',
                backupDateTime: this.utilsService.createDateTimeString(backup.dateCreated),
                size: backup.fileSize + ' bytes',
                filename: backup.filename,
                ip: adc.ipAddress,
                port: adc.port,
                namespaceId: backup.namespaceID,
                adcId: adc.id,
                adcVendorId: adc.vendor,
                applianceId: adc.applianceID
            });
        }

        this.tempRows = [...this.tableRows];
    }

    /**
     * Setup page subscribers.
     * @private
     */
    private setupSubscribers(): void {
        this.namespaceService.selectedEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (namespace: any): void => {
                    this.selectedNamespaceKey = namespace.key;
                    this.setUpPageData();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'ScheduleListComponent: setupSubscribers',
                        'Unable to get selected namespace',
                        error
                    );
                }
            });

        this.modalService.closeParentModalEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (modalId: string): void => {
                    if (modalId === 'copy-appliance-id') {
                        this.modalButtonDisabled = true;
                    }
                }
            });
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference: object = {
            'storage-backup-download': [{ path: '/api/v1/:namespace/backups/:backupID/download', methods: ['GET'] }],
            'storage-backup-delete': [
                { path: '/api/v1/:namespace/backups/:backupID', methods: ['DELETE'] },
                { path: '/api/v1/:namespace/backups/', methods: ['GET'] },
                { path: '/api/v1/:namespace/adcs/', methods: ['GET'] },
            ]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}

